import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Component, computed, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SplitterModule } from 'primeng/splitter';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Observable, of } from 'rxjs';
import { PageBaseComponent } from '../../abstracts/page-base/page-base.component';
import { SessionDetail, SessionFile } from '../../models/session';
import { User } from '../../models/user';
import { ApiService } from '../../services/api.service';
import { Parameters } from '../../utilities/parameters';
import { ProgressPoller } from '../../utilities/progress-poller';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'app-session',
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    SplitterModule,
    InputTextModule,
    TextareaModule,
    ButtonModule,
    DatePickerModule,
    TableModule,
    DialogModule,
    SelectModule,
    FileUploadModule,
    ProgressBarModule,
    InputNumberModule
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './session.component.html',
  styleUrl: './session.component.css'
})
export class SessionComponent extends PageBaseComponent<SessionFile> {
  //treeNodes: TreeNode[] = [];
  item: SessionDetail = {
    organizationId: 0,
    teamId: 0,
    sessionId: 0,
    name: '',
    description: '',
    date: new Date(),
    organization: '',
    team: '',
    sessionFiles: []
  }

  users: User[] = [];
  userOptions: { id: number; value: string; mass: number }[] = [];
  selectedUser: { id: number; value: string; mass: number } = { id: 0, value: '', mass: 1.0 };

  sessionFiles: { uuid: string, id: number, user: string, head: number, dx: number, sx: number, toElaborate: boolean, toShow: boolean, isElaborating: boolean, progress: number}[] = [];

  fileStatusMap: Record<number, string> = {
    0: 'text-red-500 far fa-ban',
    1: 'text-green-500 fas fa-check',
    2: 'text-green-500 fas fa-gears',
    3: 'text-blue-500 far fa-circle-check',
    4: 'text-orange-500 fas fa-triangle-exclamation'
  };

  fileHead: string = '';
  fileDx: string = '';
  fileSx: string = '';
  filesToUpload: Map<number, File> = new Map<number, File>();

  acceptedFiles: string = '.ubx';
  maxFileSize: number = 100 * 1024 * 1024; // 100MB
  uploading: boolean = false;

  sessionProgress = computed(() => this.poller.progressExecution());
  constructor(apiService: ApiService, messageService: MessageService, private route: ActivatedRoute, private router: Router,
    private confirmationService: ConfirmationService, private poller: ProgressPoller) {
    super(apiService, messageService)

    effect(() => {
      const p = this.sessionProgress();

        if (p === undefined) return;

        p.forEach(e => {
          let sf = this.sessionFiles.find(s => e.uuid === s.uuid);
          if (sf !== undefined) {
            sf.head = e.status;
            sf.dx = e.status;
            sf.sx = e.status;
            sf.progress = Math.floor(e.percentage);

            if (e.status !== 2) sf = this.SetButtonVisibility(sf);
          }
        })
      });
  }

  decodeType(type: number): string {
    const receiver = Parameters.receivers.filter(e => e.id === type);
    return receiver !== undefined ? receiver[0].description : `${type} - Undefined`;
  }

  override onCloseDialog(): void {
    super.onCloseDialog();

    this.loadData();
  }

  getFileStatus(status: number): string {
    return this.fileStatusMap[status] ?? this.fileStatusMap[0];
  }

  onDelete(event: Event, file: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Vuoi eliminare i file di ${file.user}?`,
      header: 'Conferma',
      closable: false,
      closeOnEscape: false,
      icon: 'far fa-trash-can',
      rejectButtonProps: {
        label: 'Annulla',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Cancella i File',
      },
      accept: () => {
        this.apiService.deleteSessionFiles(this.item, file.id).subscribe({
          next: () => {
            this.showSuccess("I file sono stati cancellati con successo")
            this.resetItems();
            this.loadData();
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err);
          }
        });
      },
      reject: () => {
        this.showErrorMessage("La procedura è stata annullata dall'utente!")
      },
    });
  }

  onElaborate(file: any): void {
    let sf : any = this.sessionFiles.find(f => f.uuid === file.uuid);

    if (!sf) {
      this.showErrorMessage("Ci sono erriri nella sessione dl'utente selezionato");
      return;
    }
    sf.isLoading = true;
    this.apiService.startElaboration(file.uuid).subscribe({
      next: (str) => {
        if (str && str.length > 0) {
          if (!this.poller.isRunning) this.poller.startPolling(str[0]);

          sf.uuid = str[0];
          sf.head = 2;
          sf.dx = 2;
          sf.sx = 2;

          sf = this.SetButtonVisibility(sf);

          this.showSuccess('Elaborazione avviata con successo!');
        } else {
          sf.isLoading = false;
          this.showErrorMessage('Errore nell\'avvio dell\'elaborazione!');
        }
      },
      error: (err: HttpErrorResponse) => {
        sf.isLoading = false;
        this.showError(err);
      }
    });
  }

  onFileSelect(event: any, type: number) {
    this.filesToUpload.set(type, event.files[0]);
  }

  override onSave(): void {
    this.apiService.updateSession(this.item).subscribe({
      next: () => {
        this.showDialog = false;
        this.showSuccess("La sessione è stata aggiornata correttamente");
        this.resetItems();
      },
      error: (err: HttpErrorResponse) => {
        this.showError(err);
      }
    });
  }

  onNavigate(file: any): void {
    this.router.navigate(['trip'], {
      relativeTo: this.route,
      state: { orgId: this.item.organizationId, teamId: this.item.teamId, sesId: this.item.sessionId, userId: file.id }
    })
  }

  override onShowDialog(item: SessionFile): void {
    this.fileHead = '';
    this.fileDx = '';
    this.fileSx = '';
    this.filesToUpload = new Map<number, File>();

    super.onShowDialog(item);
  }

  onUpload(): void {
    const ids: number[] = [this.item.organizationId, this.item.teamId, this.item.sessionId, this.selectedItem.userId, this.selectedItem.mass];
    const max = this.filesToUpload.size;
    let i: number = 1;
    let close: boolean = true;

    this.uploading = true;

    this.apiService.uploadData(ids).subscribe({
      next: (uuids: string[]) => {
        if (uuids.length === 1 && uuids[0].trim().length > 0) {
          this.filesToUpload.forEach((value: File, key: number) => {
            setTimeout(() => {
              const formData = new FormData();
              formData.append('file', value, value.name);
              formData.append('org', this.item.organizationId.toString());
              formData.append('team', this.item.teamId.toString());
              formData.append('ses', this.item.sessionId.toString());
              formData.append('user', this.selectedItem.userId.toString());
              formData.append('uuid', uuids[0].trim());
              formData.append('type', key.toString());

              this.apiService.uploadFile(formData).subscribe({
                next: (event) => {
                  if (event.type === HttpEventType.Response) {
                    this.showSuccess(event.statusText + ' - ' + event.body);
                  }
                },
                error: (err: HttpErrorResponse) => {
                  if (i === max) {
                    this.uploading = false;
                  } else {
                    i = i + 1;
                  }

                  this.showError(err);
                  close = false;
                },
                complete: () => {
                  if (i === max) {
                    this.uploading = false;
                    if (close) this.onCloseDialog();
                  } else {
                    i = i + 1;
                  }
                }
              });
            }, 200);
          });
        } else {
          this.uploading = false;
          this.showErrorMessage('Riferimento di sessione invalido!');
        }
      },
      error: (err: HttpErrorResponse) => {
        this.uploading = false;
        this.showError(err);
      }
    })
  }

  onUserSelected(event: any): void {
    this.selectedItem.userId = this.selectedUser.id;
    this.selectedItem.mass = this.selectedUser.mass;
  }

  protected override loadData(): void {
    this.loading = true;

    const nav = history.state;
    if (nav === undefined) {
      this.loading = false;

      this.showErrorMessage("Parametri non validi!")
      this.router.navigate(['/sessions']);

      return;
    }
    const ids: number[] = [nav.orgId, nav.teamId, nav.sesId];
    
    this.apiService.getSession(ids).subscribe({
      next: (s) => {
        this.item = s;
        this.item.date = new Date(s.date)

        //if (this.item.uuid.length > 0) this.poller.startPolling(this.item.uuid);

        this.sessionFiles = [];
        if (s.sessionFiles.length > 0) {
          s.sessionFiles.forEach((value: SessionFile, index: number, array: SessionFile[]) => {
            let sf = this.sessionFiles.find(f => f.uuid === value.uuid);
            if (!sf) {
              sf = {
                uuid: value.uuid,
                id: value.userId,
                user: value.userDescription,
                head: 0, dx: 0, sx: 0,
                toElaborate: false, toShow: false, isElaborating: false,
                progress: 0.0
              };
              this.sessionFiles.push(sf);
            }

            sf.head = value.status[0];
            sf.sx = value.status[1];
            sf.dx = value.status[2];

            sf = this.SetButtonVisibility(sf);
          });
        }

        this.apiService.getTeamUsers(ids).subscribe({
          next: (data) => {
            this.users = data;
            this.dtTrigger.next(true);
          },
          error: (err: HttpErrorResponse) => {
            this.loading = false;
            this.showError(err);
          },
          complete: () => {
            this.loading = false;
          }
        })
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.showError(err);
      }
    });
  }

  protected override loadSelectedInfo(): Observable<boolean> {
    this.userOptions = this.users.map(u => ({
      id: u.userId,
      value: `${u.familyName} ${u.name}`,
      mass: u.mass
    }));

    const userIds = new Set(this.item.sessionFiles.map(f => f.userId))
    this.userOptions = this.userOptions.filter(user => !userIds.has(user.id));
    this.userOptions = this.userOptions.sort((a, b) => a.value.localeCompare(b.value));

    return of(true);
  }

  protected override resetItems(): void {
    this.selectedItem = {
      uuid: '',
      userId: 0,
      mass: 1.0,
      userDescription: '',
      status:[0, 0, 0]
    }

    this.newItem = {
      uuid: '',
      userId: 0,
      mass: 1.0,
      userDescription: '',
      status: [0, 0, 0]
    }

    this.fileHead = '';
    this.fileDx = '';
    this.fileSx = '';
    this.filesToUpload = new Map<number, File>();
    this.selectedUser = {id: 0, value: '', mass: 1.0}
  }

  protected SetButtonVisibility(sf: any): any {
    sf.toElaborate = sf.head < 3 || sf.dx < 3 || sf.sx < 3;
    sf.isElaborating = sf.head === 2 || sf.dx === 2 || sf.sx === 2;
    sf.toShow = sf.head === 3 || sf.dx === 3 || sf.sx === 3;

    return sf;
  }
}
